import React, {useState, useEffect} from "react";
import Select, {components} from "react-select";
import {toast} from "react-toastify";
import {get, post} from "../../utils/fetchAPI";
import {
    FaFileInvoiceDollar,
    FaBuilding,
    FaHashtag,
    FaUser,
    FaEnvelope,
    FaPhone,
    FaMapMarkerAlt,
    FaBoxOpen,
    FaTrashAlt,
    FaPlusCircle,
} from "react-icons/fa";

// Shared field styling so every input/select looks the same across the form.
const inputClass =
    "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none transition";

const selectStyles = {
    control: (base, state) => ({
        ...base,
        borderRadius: "0.5rem",
        borderColor: state.isFocused ? "#14b8a6" : "#d1d5db",
        boxShadow: state.isFocused ? "0 0 0 2px rgba(20,184,166,0.20)" : "none",
        minHeight: "42px",
        "&:hover": {borderColor: "#14b8a6"},
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? "#14b8a6" : state.isFocused ? "#f0fdfa" : "#fff",
        color: state.isSelected ? "#fff" : "#1f2937",
        cursor: "pointer",
    }),
    menu: (base) => ({...base, borderRadius: "0.5rem", overflow: "hidden", zIndex: 30}),
};

const SectionHeader = ({icon: Icon, title, subtitle}) => (
    <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
        <div className="w-9 h-9 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
            <Icon className="text-lg" />
        </div>
        <div>
            <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
            {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
    </div>
);

const billingFields = [
    {key: "name", label: "Name", icon: FaUser, full: false},
    {key: "email", label: "Email", icon: FaEnvelope, full: false},
    {key: "phone", label: "Phone", icon: FaPhone, full: false},
    {key: "address", label: "Address", icon: FaMapMarkerAlt, full: true},
];

const InvoiceForm = ({onClose, fetchOrders}) => {
    const [formData, setFormData] = useState({
        organization: null,
        doctor: null,
        customInvoiceNumber: "",
        billTo: {name: "", email: "", phone: "", address: ""},
        selectedItems: [],
        channel: "admin",
        gateway: "manual",
    });

    const [doctors, setDoctors] = useState([]);
    const [items, setItems] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [selectedItemOption, setSelectedItemOption] = useState(null);


    // Fetch organizations
    useEffect(() => {
        get("/organization/list")
            .then(res => setOrganizations(res?.data?.organizations || []))
            .catch(console.log);
    }, []);

    // Fetch doctors
    useEffect(() => {
        get("all-user/list")
            .then(res => setDoctors(res?.data?.users || []))
            .catch(console.log);
    }, []);

    // Fetch items
    useEffect(() => {
        get("/order/catalog")
            .then(res => setItems(res?.data?.items || []))
            .catch(console.log);
    }, []);

    const organizationOptions = organizations.map(org => ({
        value: org._id,
        label: org.name
    }));

    const doctorOptions = doctors.map(user => ({
        value: user._id,
        label: user.name,
        email: user.email,
        phone: user.phone,
        address: user.address,
        organization: user.organization ? {value: user.organization._id, label: user.organization.name} : null,
    }));

    const CustomOption = (props) => (
        <components.Option {...props}>
            <div className="flex flex-col">
                <span className="font-medium text-gray-800">{props.data.name}</span>
                <span className="text-xs text-gray-500">
                    Rate: ৳{props.data.unitPrice.toFixed(2)}
                </span>
            </div>
        </components.Option>
    );

    const itemOptions = items.map(item => ({
        value: item.code,
        name: item.name,
        unitPrice: parseFloat(item.unitPrice),
        label: item.name, // fallback display
    }));

    // Select handlers
    const handleOrganizationChange = (org) => setFormData({...formData, organization: org});

    const handleDoctorChange = (doctor) => {
        setFormData({
            ...formData,
            doctor,
            organization: doctor.organization || formData.organization, // auto select org
            billTo: {
                name: doctor.label,
                email: doctor.email || "",
                phone: doctor.phone || "",
                address: doctor.address || "",
            },
        });
    };

    const handleAddItem = (item) => {
        if (!item) return;

        setFormData(prev => {
            if (prev.selectedItems.some(i => i.value === item.value)) {
                return prev;
            }

            return {
                ...prev,
                selectedItems: [
                    ...prev.selectedItems,
                    {
                        ...item,
                        quantity: 1,
                        discount: 0,
                        description: "",
                        rate: Number(item.unitPrice) || 0, // 🔥 FIX
                    },
                ],
            };
        });

        setSelectedItemOption(null);
    };


    const handleItemChange = (index, field, value) => {
        const updated = [...formData.selectedItems];
        updated[index][field] = value;
        setFormData({...formData, selectedItems: updated});
    };

    const handleRemoveItem = (index) => {
        const updatedItems = [...formData.selectedItems];
        updatedItems.splice(index, 1); // remove the item
        setFormData({...formData, selectedItems: updatedItems});
    };


    // Submit form
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.doctor) return toast.error("Please select a doctor");
        if (formData.selectedItems.length === 0) return toast.error("Please add at least one item");

        const payload = {
            organization: formData.organization?.value || undefined, // string ID, optional
            doctor: formData.doctor.value, // string ID
            customInvoiceNumber: formData.customInvoiceNumber?.trim() || "",
            billTo: formData.billTo,
            channel: "admin",
            gateway: "manual",
            items: formData.selectedItems.map(it => ({
                code: it.value, // string code
                quantity: it.quantity,
                discount: it.discount.toString(), // convert to string
                description: it.description || "",
            })),
        };

        try {
            await post("/order/create", payload);
            toast.success("Order created successfully!");
            fetchOrders?.();
            onClose?.();
            window.location.href = "/order-list"; // redirect after creation
        } catch (error) {
            console.error("Error creating order:", error);
            const msg =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                "Failed to create order. Please check console for details.";
            toast.error(msg);
        }
    };

    const summary = formData.selectedItems.reduce(
        (acc, item) => {
            const itemTotal = item.quantity * item.rate;
            acc.totalQuantity += item.quantity;
            acc.subTotal += itemTotal;
            acc.totalDiscount += item.discount;
            acc.grandTotal += itemTotal - item.discount;
            return acc;
        },
        {
            totalQuantity: 0,
            subTotal: 0,
            totalDiscount: 0,
            grandTotal: 0,
        }
    );

    const hasItems = formData.selectedItems.length > 0;

    return (
        <div className="max-w-5xl mx-auto pb-10">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-600 to-blue-500 rounded-xl px-6 py-5 mb-6 shadow-md flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                    <FaFileInvoiceDollar className="text-white text-2xl" />
                </div>
                <div>
                    <h1 className="text-white text-xl font-bold leading-tight">Create Invoice</h1>
                    <p className="text-white/80 text-sm">Generate a new order &amp; invoice</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Order details */}
                <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    <SectionHeader icon={FaBuilding} title="Order Details" subtitle="Who is this invoice for?" />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Organization <span className="text-gray-400 font-normal">(optional)</span>
                            </label>
                            <Select
                                options={organizationOptions}
                                value={formData.organization}
                                onChange={handleOrganizationChange}
                                placeholder="Select an organization..."
                                styles={selectStyles}
                                isClearable
                                isSearchable
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Doctor</label>
                            <Select
                                options={doctorOptions}
                                value={formData.doctor}
                                onChange={handleDoctorChange}
                                placeholder="Select a doctor..."
                                styles={selectStyles}
                                isSearchable
                            />
                        </div>
                    </div>

                    <div className="mt-5">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Custom Invoice Number <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <div className="relative">
                            <FaHashtag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                            <input
                                type="text"
                                className={`${inputClass} pl-9`}
                                placeholder="e.g. INV-2026-001"
                                value={formData.customInvoiceNumber}
                                onChange={(e) =>
                                    setFormData({...formData, customInvoiceNumber: e.target.value})
                                }
                            />
                        </div>
                        <p className="text-xs text-gray-400 mt-1.5">
                            Leave blank to use the system invoice number. You can set or change it later while editing the order.
                        </p>
                    </div>
                </section>

                {/* Bill To */}
                <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    <SectionHeader icon={FaUser} title="Billing To" subtitle="Contact the invoice is addressed to" />

                    {formData.doctor ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {billingFields.map(({key, label, icon: Icon, full}) => (
                                <div key={key} className={full ? "md:col-span-2" : ""}>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                                    <div className="relative">
                                        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                                        <input
                                            className={`${inputClass} pl-9`}
                                            placeholder={label}
                                            value={formData.billTo[key]}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    billTo: {...formData.billTo, [key]: e.target.value},
                                                })
                                            }
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                            Select a doctor above to auto-fill the billing details.
                        </div>
                    )}
                </section>

                {/* Items */}
                <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    <SectionHeader icon={FaBoxOpen} title="Items" subtitle="Add the products or services to bill" />

                    <div className="mb-5">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Add Item</label>
                        <Select
                            options={itemOptions}
                            value={selectedItemOption}
                            onChange={(item) => {
                                setSelectedItemOption(item);
                                handleAddItem(item);
                            }}
                            placeholder="Search and select an item..."
                            components={{Option: CustomOption}}
                            styles={selectStyles}
                            isClearable
                            isSearchable
                        />
                    </div>

                    {!hasItems ? (
                        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                            <FaBoxOpen className="mx-auto text-3xl text-gray-300 mb-2" />
                            <p className="text-sm font-medium text-gray-500">No items added yet</p>
                            <p className="text-xs text-gray-400 mt-1">Use the selector above to add your first item.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-lg border border-gray-200">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                                        <th className="text-left font-semibold px-4 py-3">Item</th>
                                        <th className="text-center font-semibold px-4 py-3 w-24">Qty</th>
                                        <th className="text-right font-semibold px-4 py-3 w-28">Rate</th>
                                        <th className="text-center font-semibold px-4 py-3 w-28">Discount</th>
                                        <th className="text-right font-semibold px-4 py-3 w-32">Amount</th>
                                        <th className="text-center font-semibold px-4 py-3 w-16">&nbsp;</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {formData.selectedItems.map((item, index) => {
                                        const quantity = Number(item.quantity) || 0;
                                        const rate = Number(item.rate) || 0;
                                        const discount = Number(item.discount) || 0;
                                        const amount = Math.max(quantity * rate - discount, 0);

                                        return (
                                            <tr key={index} className="hover:bg-gray-50/70 transition">
                                                <td className="px-4 py-3 align-top">
                                                    <div className="font-medium text-gray-800">{item.label}</div>
                                                    <input
                                                        type="text"
                                                        className="mt-1.5 w-full rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 placeholder-gray-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 focus:outline-none"
                                                        placeholder="Add a description (optional)"
                                                        value={item.description || ""}
                                                        onChange={(e) =>
                                                            handleItemChange(index, "description", e.target.value)
                                                        }
                                                    />
                                                </td>
                                                <td className="px-4 py-3 text-center align-top">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        className="w-16 rounded-md border border-gray-300 px-2 py-1 text-center text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 focus:outline-none"
                                                        value={quantity}
                                                        onChange={(e) =>
                                                            handleItemChange(index, "quantity", Number(e.target.value) || 0)
                                                        }
                                                    />
                                                </td>
                                                <td className="px-4 py-3 text-right align-top text-gray-600">
                                                    ৳{rate.toFixed(2)}
                                                </td>
                                                <td className="px-4 py-3 text-center align-top">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        className="w-20 rounded-md border border-gray-300 px-2 py-1 text-center text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500/30 focus:outline-none"
                                                        value={discount}
                                                        onChange={(e) =>
                                                            handleItemChange(index, "discount", Number(e.target.value) || 0)
                                                        }
                                                    />
                                                </td>
                                                <td className="px-4 py-3 text-right align-top font-semibold text-gray-900">
                                                    ৳{amount.toFixed(2)}
                                                </td>
                                                <td className="px-4 py-3 text-center align-top">
                                                    <button
                                                        type="button"
                                                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-red-500 hover:bg-red-50 transition"
                                                        onClick={() => handleRemoveItem(index)}
                                                        title="Remove item"
                                                    >
                                                        <FaTrashAlt />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                {/* Summary + actions */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <button
                        type="submit"
                        className="order-2 md:order-1 inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-teal-600 to-blue-500 text-white text-sm font-semibold rounded-lg shadow-md hover:shadow-lg hover:opacity-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!hasItems}
                    >
                        <FaPlusCircle />
                        Create Order
                    </button>

                    <div className="order-1 md:order-2 w-full md:w-80 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                        <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-100">
                            <h4 className="text-sm font-semibold text-gray-700">Order Summary</h4>
                        </div>
                        <div className="p-4 space-y-2.5">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Total Quantity</span>
                                <span className="font-medium text-gray-800">{summary.totalQuantity}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Sub Total</span>
                                <span className="font-medium text-gray-800">৳{summary.subTotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Total Discount</span>
                                <span className="font-medium text-red-500">− ৳{summary.totalDiscount.toFixed(2)}</span>
                            </div>
                            <div className="border-t border-gray-100 pt-2.5 flex justify-between items-center">
                                <span className="text-sm font-semibold text-gray-700">Grand Total</span>
                                <span className="text-lg font-bold text-teal-600">৳{summary.grandTotal.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default InvoiceForm;
